import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useEffect, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { useToast } from '@ais-ui/ui-core';
import { useSaveSettings } from '@ais-ui/websockets';
import {
  useTimeZoneContext,
  CursorPositionFormat,
  TimeZone,
} from '@ais-ui/utils';
import { useSettings } from '../SettingsProvider';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { useSettingsPage } from '../SettingsPageProvider';
import { getLengthUnit } from '../../Units/utils';

// Combined schema for all general settings
export const generalFormSchema = z.object({
  // Formatting
  dateFormat: z.string().optional(),
  timeZone: z.string().optional(),
  showCursorPosition: z.boolean().optional(),
  cursorPositionFormat: z.string().optional(),
  // Units
  lengthUnit: z.string(),
  // Layout
  isSidebarExpanded: z.boolean(),
  tableDensity: z.enum(['compact', 'normal']).optional(),
  // Remote Control
  isRemoteControlEnabled: z.boolean(),
  // Map Scale
  isMapScaleEnabled: z.boolean(),
  // Zoom Buttons
  areZoomButtonsEnabled: z.boolean(),
});

export type GeneralFormValues = z.infer<typeof generalFormSchema>;

export const useGeneralForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();
  const { timeZone } = useTimeZoneContext();
  const { userDateFormat } = useDateTimeFormat();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { mutate: mutateSettings } = useSaveSettings();
  const { setUnsavedSettings } = useSettingsPage();

  const getTimeZonesPlusLocalUserTimeZone = (): string[] => {
    const predefinedTimeZones = Object.values(TimeZone) as string[];
    if (predefinedTimeZones.includes(timeZone)) {
      return predefinedTimeZones;
    }
    return [...predefinedTimeZones, timeZone];
  };

  const initialValues: GeneralFormValues = {
    // Formatting
    dateFormat: userDateFormat,
    timeZone: timeZone,
    showCursorPosition:
      settings['ribbonPlugin.settings'].show_cursor_position || false,
    cursorPositionFormat:
      settings['ribbonPlugin.settings'].cursor_position_format ||
      CursorPositionFormat.positionDDMM,
    // Units
    lengthUnit: getLengthUnit(settings),
    // Layout
    isSidebarExpanded:
      settings['ribbonPlugin.settings']?.is_sidebar_expanded ?? true,
    tableDensity: settings['ribbonPlugin.settings']?.table_density ?? 'normal',
    // Remote Control
    isRemoteControlEnabled:
      settings['ribbonPlugin.settings']?.remotecontrol_enabled ?? false,
    // Map Scale
    isMapScaleEnabled:
      settings['ribbonPlugin.settings']?.mapscale_enabled ?? false,
    // Zoom Buttons
    areZoomButtonsEnabled:
      settings['ribbonPlugin.settings']?.zoom_buttons_enabled ?? false,
  };

  const form = useForm<GeneralFormValues>({
    resolver: zodResolver(generalFormSchema),
    defaultValues: initialValues,
  });

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  const onSubmit = (data: GeneralFormValues) => {
    setIsSubmitting(true);

    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        // Formatting
        date_format_mp: data.dateFormat,
        time_zone: data.timeZone,
        show_cursor_position: data.showCursorPosition,
        cursor_position_format: data.cursorPositionFormat,
        // Units
        length_unit: data.lengthUnit,
        // Layout
        is_sidebar_expanded: data.isSidebarExpanded,
        table_density: data.tableDensity ?? 'normal',
        // Remote Control
        remotecontrol_enabled: data.isRemoteControlEnabled,
        // Map Scale
        mapscale_enabled: data.isMapScaleEnabled,
        // Zoom Buttons
        zoom_buttons_enabled: data.areZoomButtonsEnabled,
      },
    };

    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-general-settings-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsGeneralSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          className: 'update-general-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsGeneralFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  };

  return {
    form,
    onSubmit,
    isSubmitting,
    getTimeZonesPlusLocalUserTimeZone,
    initialValues,
  };
};
